import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { BottomNavigation } from '../components/BottomNavigation';
import { Edit, ArrowLeft, Plus, TickSquare } from 'react-iconly';
import { getUserProfile, updateUserProfile } from '../utils/userApi';
import { uploadProfileImage } from '../utils/imageUpload';
import { BackButton } from '../components/ui';
import { COLORS } from '../lib/styleGuide';

// Style guide colors
const PRIMARY_BROWN = '#C97B53';
const PRIMARY_BROWN_DARK = '#A86A46';
const NEUTRAL_BG = '#F9F9F9';
const NEUTRAL_WHITE = '#FFF';
const NEUTRAL_TEXT = '#222';
const ACCENT_GRAY = '#757575';
const ERROR_RED = '#F44336';
const SUCCESS_GREEN = '#4CAF50';

const headingFont = {
  fontFamily: 'Sora, sans-serif',
  fontWeight: 700,
};
const bodyFont = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 500,
};

const defaultProfile = {
  fullName: '',
  phone: '',
  email: '',
  address: {
    street: '',
    apt: '',
    city: '',
    state: '',
    zip: ''
  },
  dietaryRestrictions: [],
  allergies: [],
  paymentMethods: [],
  profilePic: '',
  distancePreference: 10, // Default to 10 miles
};

const dietaryOptions = [
  'Vegetarian', 'Vegan', 'Gluten-Free', 'Dairy-Free', 'Halal', 'Kosher'
];
const allergyOptions = [
  'Nuts', 'Shellfish', 'Eggs', 'Soy', 'Wheat', 'Fish', 'Milk', 'Peanuts', 'Sesame'
];

function TagInput({ label, value, options, onChange, placeholder }) {
  const [input, setInput] = useState('');
  const addTag = (tag) => {
    if (tag && !value.includes(tag)) onChange([...value, tag]);
    setInput('');
  };
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 12, color: ACCENT_GRAY, fontWeight: 600, marginBottom: 4 }}>{label}</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
        {value.map((tag, i) => (
          <span key={tag} style={{ background: PRIMARY_BROWN + '22', color: PRIMARY_BROWN, borderRadius: 12, padding: '4px 12px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
            {tag}
            <button aria-label={`Remove ${tag}`} onClick={() => onChange(value.filter((t, idx) => idx !== i))} style={{ background: 'none', border: 'none', color: PRIMARY_BROWN, cursor: 'pointer', fontSize: 12, marginLeft: 2 }}>×</button>
          </span>
        ))}
      </div>
      <input
        type="text"
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' && input.trim()) {
            addTag(input.trim());
            e.preventDefault();
          }
        }}
        list={label + '-options'}
        placeholder={placeholder}
        style={{ width: '100%', padding: '10px', border: '1px solid #E0E0E0', borderRadius: 8, fontSize: 15, ...bodyFont }}
        aria-label={label}
      />
      <datalist id={label + '-options'}>
        {options.map(opt => <option key={opt} value={opt} />)}
      </datalist>
    </div>
  );
}

const Profile = () => {
  const { currentUser, userRole } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef();
  const [profile, setProfile] = useState(defaultProfile);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [unsaved, setUnsaved] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profileImageFile, setProfileImageFile] = useState(null);

  // Fetch user profile from Firestore
  useEffect(() => {
    async function fetchProfile() {
      setLoading(true);
      try {
        const data = await getUserProfile(currentUser.uid);
        if (data) {
          setProfile({
            ...defaultProfile,
            ...data,
            address: { ...defaultProfile.address, ...data.address }
          });
        } else {
          setProfile({
            ...defaultProfile,
            fullName: currentUser?.displayName || '',
            phone: currentUser?.phoneNumber || '',
            email: currentUser?.email || '',
            profilePic: '' // Ensure profilePic is explicitly set to empty string
          });
        }
      } catch (e) {
        setError('Failed to load profile.');
      }
      setLoading(false);
    }
    if (currentUser?.uid) fetchProfile();
    // eslint-disable-next-line
  }, [currentUser]);

  // Track unsaved changes
  useEffect(() => { setUnsaved(true); }, [profile]);

  const handleInput = (field, value) => setProfile(p => ({ ...p, [field]: value }));
  const handleAddress = (field, value) => setProfile(p => ({ ...p, address: { ...p.address, [field]: value } }));

  // Profile picture upload (placeholder logic)
  const handleProfilePic = e => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file.');
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image file size must be less than 5MB.');
        return;
      }
      
      setProfileImageFile(file);
      setProfile(p => ({ ...p, profilePic: URL.createObjectURL(file) }));
      setError(''); // Clear any previous errors
    }
  };

  // Save changes to Firestore
  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      // Upload profile image if a new one was selected
      let finalProfilePicUrl = profile.profilePic;
      if (profileImageFile) {
        try {
          const uploadResult = await uploadProfileImage(profileImageFile, currentUser.uid);
          finalProfilePicUrl = uploadResult.url;
          // Update the profile with the permanent URL
          setProfile(p => ({ ...p, profilePic: finalProfilePicUrl }));
        } catch (uploadError) {
          console.error('Failed to upload profile image:', uploadError);
          setError('Failed to upload profile image. Please try again.');
          setSaving(false);
          return;
        }
      }

      // Save profile data with the permanent image URL
      const profileDataToSave = {
        ...profile,
        profilePic: finalProfilePicUrl
      };
      
      await updateUserProfile(currentUser.uid, profileDataToSave);
      setSuccess('Profile updated successfully!');
      setUnsaved(false);
      setProfileImageFile(null); // Clear the file reference
    } catch (e) {
      setError('Error saving changes. Please try again.');
    }
    setSaving(false);
  };

  // Payment methods (placeholder logic)
  const handleAddPayment = () => {
    setProfile(p => ({
      ...p,
      paymentMethods: [...p.paymentMethods, { id: `pm_${Date.now()}`, brand: 'Mastercard', last4: '5555', exp: '01/28' }]
    }));
  };
  const handleRemovePayment = id => {
    setProfile(p => ({ ...p, paymentMethods: p.paymentMethods.filter(pm => pm.id !== id) }));
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;

  return (
    <div style={{ minHeight: '100vh', background: NEUTRAL_BG, paddingBottom: 96 }}>
      {/* Header */}
      <header style={{ position: 'sticky', top: 0, zIndex: 10, background: NEUTRAL_WHITE, borderBottom: '1px solid #eee', padding: '12px 0 0 0', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '0 20px 12px 20px' }}>
          <BackButton
            onClick={() => navigate(-1)}
            variant="standard"
            ariaLabel="Back"
          />
  
        </div>
      </header>

      {/* Profile Picture */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24 }}>
        <div 
          onClick={() => fileInputRef.current?.click()}
          style={{ 
            position: 'relative', 
            width: 104, 
            height: 104, 
            marginBottom: 8, 
            background: NEUTRAL_BG, 
            borderRadius: '50%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'transform 0.2s ease',
            ':hover': {
              transform: 'scale(1.05)'
            }
          }}
        >
          {profile.profilePic && profile.profilePic.trim() !== '' ? (
            <img
              src={profile.profilePic}
              alt="Profile"
              style={{ width: 104, height: 104, borderRadius: '50%', objectFit: 'cover', border: `3px solid ${PRIMARY_BROWN}` }}
            />
          ) : (
            <span style={{
              width: 104,
              height: 104,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              fontSize: 40,
              fontWeight: 700,
              color: PRIMARY_BROWN,
              background: NEUTRAL_BG,
              border: `3px solid ${PRIMARY_BROWN}`,
              fontFamily: 'Sora, sans-serif',
              textTransform: 'uppercase',
              letterSpacing: 2
            }}>
              {(profile.fullName || 'User').split(' ').map(w => w[0]).join('').slice(0,2)}
            </span>
          )}
          <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleProfilePic} />
        </div>
        <div style={{ fontSize: 15, color: ACCENT_GRAY, ...bodyFont }}>Tap to change photo</div>
      </div>

      {/* Form Sections */}
      <form onSubmit={e => { e.preventDefault(); handleSave(); }} style={{ maxWidth: 440, margin: '0 auto', background: NEUTRAL_WHITE, borderRadius: 20, boxShadow: '0 2px 8px #0001', padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* Personal Info */}
        <section>
          <h2 style={{ ...headingFont, fontSize: 16, color: NEUTRAL_TEXT, marginBottom: 16 }}>Personal Information</h2>
                      <label style={{ fontSize: 12, color: ACCENT_GRAY, fontWeight: 600 }}>Full Name</label>
          <input type="text" value={profile.fullName} onChange={e => handleInput('fullName', e.target.value)} style={{ width: '100%', padding: '10px', border: '1px solid #E0E0E0', borderRadius: 8, fontSize: 15, marginBottom: 12, ...bodyFont }} required aria-label="Full Name" />
                      <label style={{ fontSize: 12, color: ACCENT_GRAY, fontWeight: 600 }}>Phone</label>
          <input type="tel" value={profile.phone} onChange={e => handleInput('phone', e.target.value)} style={{ width: '100%', padding: '10px', border: '1px solid #E0E0E0', borderRadius: 8, fontSize: 15, marginBottom: 12, ...bodyFont }} aria-label="Phone" />
                      <label style={{ fontSize: 12, color: ACCENT_GRAY, fontWeight: 600 }}>Email</label>
          <input type="email" value={profile.email} onChange={e => handleInput('email', e.target.value)} style={{ width: '100%', padding: '10px', border: '1px solid #E0E0E0', borderRadius: 8, fontSize: 15, marginBottom: 0, ...bodyFont }} required aria-label="Email" />
        </section>
        {/* Address */}
        <section>
          <h2 style={{ ...headingFont, fontSize: 16, color: NEUTRAL_TEXT, marginBottom: 16 }}>Delivery Address</h2>
                      <label style={{ fontSize: 12, color: ACCENT_GRAY, fontWeight: 600 }}>Street Address</label>
          <input type="text" value={profile.address.street} onChange={e => handleAddress('street', e.target.value)} style={{ width: '100%', padding: '10px', border: '1px solid #E0E0E0', borderRadius: 8, fontSize: 15, marginBottom: 12, ...bodyFont }} required aria-label="Street Address" />
                      <label style={{ fontSize: 12, color: ACCENT_GRAY, fontWeight: 600 }}>Apt/Suite (Optional)</label>
          <input type="text" value={profile.address.apt} onChange={e => handleAddress('apt', e.target.value)} style={{ width: '100%', padding: '10px', border: '1px solid #E0E0E0', borderRadius: 8, fontSize: 15, marginBottom: 12, ...bodyFont }} aria-label="Apt/Suite" />
                      <label style={{ fontSize: 12, color: ACCENT_GRAY, fontWeight: 600 }}>City</label>
          <input type="text" value={profile.address.city} onChange={e => handleAddress('city', e.target.value)} style={{ width: '100%', padding: '10px', border: '1px solid #E0E0E0', borderRadius: 8, fontSize: 15, marginBottom: 12, ...bodyFont }} required aria-label="City" />
                      <label style={{ fontSize: 12, color: ACCENT_GRAY, fontWeight: 600 }}>State</label>
          <input type="text" value={profile.address.state} onChange={e => handleAddress('state', e.target.value)} style={{ width: '100%', padding: '10px', border: '1px solid #E0E0E0', borderRadius: 8, fontSize: 15, marginBottom: 12, ...bodyFont }} required aria-label="State" />
                      <label style={{ fontSize: 12, color: ACCENT_GRAY, fontWeight: 600 }}>ZIP Code</label>
          <input type="text" value={profile.address.zip} onChange={e => handleAddress('zip', e.target.value)} style={{ width: '100%', padding: '10px', border: '1px solid #E0E0E0', borderRadius: 8, fontSize: 15, marginBottom: 0, ...bodyFont }} required aria-label="ZIP Code" />
        </section>
        {/* Dietary Preferences */}
        <section>
          <h2 style={{ ...headingFont, fontSize: 16, color: NEUTRAL_TEXT, marginBottom: 16 }}>Dietary Preferences</h2>
          <TagInput label="Dietary Restrictions" value={profile.dietaryRestrictions} options={dietaryOptions} onChange={v => handleInput('dietaryRestrictions', v)} placeholder="Add restriction..." />
          <TagInput label="Allergies" value={profile.allergies} options={allergyOptions} onChange={v => handleInput('allergies', v)} placeholder="Add allergy..." />
        </section>
        {/* Preferences */}
        <section>
          <h2 style={{ ...headingFont, fontSize: 16, color: NEUTRAL_TEXT, marginBottom: 16 }}>Preferences</h2>
          <div style={{ marginBottom: 16 }}>
                          <label style={{ display: 'block', fontSize: 12, color: ACCENT_GRAY, fontWeight: 600, marginBottom: 8 }}>
              Distance Preference
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <input
                type="range"
                min="1"
                max="25"
                value={profile.distancePreference}
                onChange={e => handleInput('distancePreference', parseInt(e.target.value))}
                style={{
                  flex: 1,
                  height: 6,
                  borderRadius: 3,
                  background: `linear-gradient(to right, ${PRIMARY_BROWN} 0%, ${PRIMARY_BROWN} ${(profile.distancePreference - 1) / 24 * 100}%, #E0E0E0 ${(profile.distancePreference - 1) / 24 * 100}%, #E0E0E0 100%)`,
                  outline: 'none',
                  WebkitAppearance: 'none',
                  appearance: 'none',
                }}
                aria-label="Distance preference slider"
              />
              <span style={{ 
                fontSize: 14, 
                fontWeight: 600, 
                color: NEUTRAL_TEXT, 
                minWidth: 40,
                textAlign: 'right',
                ...bodyFont 
              }}>
                {profile.distancePreference} mi
              </span>
            </div>
            <p style={{ fontSize: 13, color: ACCENT_GRAY, margin: 0, ...bodyFont }}>
              Maximum distance for food search results
            </p>
          </div>
        </section>
        {/* Payment Methods */}
        <section>
          <h2 style={{ ...headingFont, fontSize: 16, color: NEUTRAL_TEXT, marginBottom: 16 }}>Payment Methods</h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, marginBottom: 12 }}>
            {profile.paymentMethods.map(pm => (
              <li key={pm.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: NEUTRAL_BG, borderRadius: 10, padding: '10px 16px', marginBottom: 8 }}>
                <span style={{ fontSize: 15, color: NEUTRAL_TEXT }}>{pm.brand} •••• {pm.last4} <span style={{ color: ACCENT_GRAY, fontSize: 13 }}>Exp {pm.exp}</span></span>
                <button onClick={() => handleRemovePayment(pm.id)} aria-label="Remove payment method" style={{ background: 'none', border: 'none', color: ERROR_RED, fontWeight: 700, fontSize: 16, cursor: 'pointer' }}>×</button>
              </li>
            ))}
          </ul>
          <button type="button" onClick={handleAddPayment} style={{ background: 'none', color: PRIMARY_BROWN, border: 'none', borderRadius: 8, padding: '8px', fontSize: 15, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Plus set="bold" size={24} primaryColor={PRIMARY_BROWN} />
          </button>
        </section>
        {/* Seller CTA */}
        {userRole !== 'creator' && (
          <section style={{ marginTop: 8 }}>
            <button type="button" onClick={() => navigate('/onboarding')} style={{
              background: NEUTRAL_WHITE,
              color: PRIMARY_BROWN,
              border: `2px solid ${PRIMARY_BROWN}`,
              borderRadius: 10,
              padding: '14px 0',
              fontSize: 14,
              fontWeight: 700,
              width: '100%',
              marginBottom: 0,
              cursor: 'pointer',
              ...bodyFont
            }}>
              Start a Shop
            </button>
          </section>
        )}
        {/* Save Changes Button */}
        <div style={{ position: 'sticky', bottom: 0, left: 0, right: 0, background: NEUTRAL_WHITE, padding: '18px 0 0 0', zIndex: 20, marginTop: 24 }}>
          <button type="submit" disabled={saving} style={{ 
            width: '100%', 
            background: COLORS.PRIMARY_GRADIENT,
            backgroundSize: '200% 200%',
            animation: 'gradientShift 3s ease infinite',
            color: '#fff', 
            border: 'none', 
            borderRadius: 12, 
            padding: '16px 0', 
            fontSize: 16, 
            fontWeight: 700, 
            cursor: saving ? 'not-allowed' : 'pointer', 
            boxShadow: '0 2px 8px #0001', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: 10, 
            transition: 'all 0.3s ease',
            ...bodyFont 
          }}>
            <TickSquare set="bold" size={22} primaryColor="#fff" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          {error && <div style={{ color: ERROR_RED, fontSize: 15, marginTop: 8 }}>{error}</div>}
          {success && <div style={{ color: SUCCESS_GREEN, fontSize: 15, marginTop: 8 }}>{success}</div>}
        </div>
      </form>
      <BottomNavigation />
    </div>
  );
};

export default Profile; 